import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import "./InventoryPreview.css";
import ColorParse from "../ColorParse";
import unknownTexture from "../../../../images/editor/unknown.png";
import { getCustomHeadTexture } from "./CustomHeads";

export default function InventoryPreview({ config, setConfig, textures, selectedItem, setSelectedItem }) {
  // Clamp row count between 1 and 6, falling back to 1 if the config value is invalid
  const rowCount = Math.min(Math.max(!isNaN(Math.round(Number(config?.rows))) ? Math.round(Number(config?.rows)) : 1, 1), 6);
  const columns = 9; // Minecraft chest inventories are always 9 columns wide
  const totalSlots = rowCount * columns;

  // Track which slot index the mouse is currently hovering over (for showing slot controls)
  const [hoveredSlot, setHoveredSlot] = useState(null);

  // Ref to the outer chest element, used to calculate the responsive scale
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Tooltip state: visibility flag, cursor position, and the rendered JSX content to display
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });

  // Ref attached to the tooltip DOM element so we can measure its rendered width
  const tooltipRef = useRef(null);
  // The final computed left position for the tooltip after overflow checking
  // null means the tooltip hasn't been measured yet and should stay hidden
  const [tooltipLeft, setTooltipLeft] = useState(null);

  // The design width the chest UI was built against — used to compute the responsive scale factor
  const BASE_CHEST_WIDTH = 384;

  // Recalculate the CSS --scale variable whenever the parent container resizes,
  // keeping the chest UI proportionally sized within its parent
  useEffect(() => {
    function updateScale() {
      if (!containerRef.current) return;
      const parentWidth = containerRef.current.parentElement.offsetWidth;

      // Scale so the chest takes up 70% of the parent's width
      let newScale = (parentWidth * 0.70) / BASE_CHEST_WIDTH;

      const MIN_SCALE = 1;
      const MAX_SCALE = 2.75;

      newScale = Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE);

      setScale(newScale);
    }

    window.addEventListener("resize", updateScale);
    updateScale(); // Run once on mount to set the initial scale

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  // Build a flat array of length totalSlots where each entry is either null (empty)
  // or { item, key } for whichever item belongs in that slot
  const slotItems = Array(totalSlots).fill(null);

  // Populate slotItems from config.layout, which maps slot indices to item key arrays.
  // Only the first item key in each array is used (menus support multiple items per slot
  // for randomisation, but the preview just shows the first)
  if (config?.layout && typeof config.layout === "object") {
    for (const [slotKey, itemArray] of Object.entries(config.layout)) {
      const slotIndex = parseInt(slotKey, 10);
      if (
        !isNaN(slotIndex) &&
        slotIndex >= 0 &&
        slotIndex < totalSlots &&
        Array.isArray(itemArray) &&
        itemArray.length > 0
      ) {
        const itemKey = itemArray[0];
        const item = config.items?.[itemKey];
        if (item) {
          slotItems[slotIndex] = { item, key: itemKey };
        }
      }
    }
  }

  // Track which slots were filled by the fill item so we can treat them differently
  // (fill slots don't show the remove button, and can always be overwritten)
  const fillIndices = new Set();
  if (config?.layout?.fill?.length > 0) {
    const fillItemKey = config.layout.fill[0];
    const fillItem = config.items?.[fillItemKey];

    if (fillItem) {
      // Place the fill item in every slot that doesn't already have an explicit item
      for (let i = 0; i < totalSlots; i++) {
        if (!slotItems[i]) {
          slotItems[i] = { item: fillItem, key: fillItemKey };
          fillIndices.add(i);
        }
      }
    }
  }

  // Tags whose textures can't be resolved at runtime — always render as the unknown/question-mark texture
  const fixedTagSet = new Set(["[itemsadder]", "[nexo]", "[hdb]", "[mmo]"]);

  // Resolve a material string to a texture object ({ url, isHead? }).
  // Handles special bracket tags like [head], [itemsadder], etc.,
  // then falls back to matching against the loaded vanilla texture list by filename.
  const getItemTexture = (material) => {
    if (!material || typeof material !== "string") return null;

    // Check if the material uses a plugin tag, e.g. "[head] Notch" or "[itemsadder] my_item"
    const tagMatch = material.match(/^\[(\w+)]\s*(.*)$/);
    if (tagMatch) {
      const tag = `[${tagMatch[1].toLowerCase()}]`;
      const value = tagMatch[2];

      // [head] tags: fetch the player skin head texture by username/value
      if (tag === "[head]") {
        return getCustomHeadTexture(value);
      }

      // Other plugin tags we can't resolve: show the unknown texture placeholder
      if (fixedTagSet.has(tag)) {
        return { url: unknownTexture };
      }
    }

    // Standard material: look for a matching "<material>.webp" in the loaded texture list
    // Strip trailing component data e.g. "ENDER_PEARL[stack_size:99]" → "ENDER_PEARL".
    const baseMaterial = material.replace(/\[.*$/, "").trim();

    const fileName = `${baseMaterial.toLowerCase()}.webp`;
    return textures?.find((t) => t.fileName === fileName) || null;
  };

  // After every tooltip state change, measure the tooltip's rendered width and decide
  // whether it would overflow the right edge of the viewport.
  // useLayoutEffect runs synchronously after DOM mutation but BEFORE the browser paints,
  // so the repositioning is invisible to the user — no flicker.
  useLayoutEffect(() => {
    if (tooltip.visible && tooltipRef.current) {
      const { width } = tooltipRef.current.getBoundingClientRect();
      const wouldOverflow = tooltip.x + width > window.innerWidth - 8;
      // If it would overflow, place the tooltip to the LEFT of the cursor instead
      setTooltipLeft(wouldOverflow ? tooltip.x - width - 24 : tooltip.x);
    } else {
      // Reset when the tooltip hides so the next item starts hidden again
      setTooltipLeft(null);
    }
  }, [tooltip]);

  // Determine whether a tooltip should be shown for a given slot.
  // Fill slots are skipped (too noisy), and items can opt out via tooltip: false.
  const shouldShowTooltip = (item, isFillSlot) => {
    if (!item || isFillSlot || item.tooltip === false) return false;
    return true;
  };

  // Build the JSX content for the tooltip: a name line and optional lore lines.
  // ColorParse converts Minecraft colour codes (e.g. §a, &a) to styled <span> elements.
  const getTooltipContent = (item) => {
    if (!item) return null;

    const hasName = item.name && item.name.trim() !== "";
    // Use the configured display name if set, otherwise derive a readable name from the material ID
    const displayName = hasName
      ? ColorParse(item.name)
      : formatMaterialName(item.material || "Unknown");

    return (
      <div className="tooltip-content">
        <div className="tooltip-name">{displayName}</div>
        {item.lore && Array.isArray(item.lore) && (
          <div className="tooltip-lore">
            {item.lore.map((line, idx) => (
              <div key={idx}>{line.trim() === "" ? "\u00A0" : ColorParse(line)}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Convert a snake_case material ID like "diamond_sword" into "Diamond Sword"
  const formatMaterialName = (material) => {
    return material
      .toLowerCase()
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Keep the tooltip's x/y in sync with the cursor as the mouse moves across a slot.
  // Only updates position — the content doesn't change until the user enters a new slot.
  const handleMouseMove = (e) => {
    setTooltip((prev) => ({
      ...prev,
      x: e.clientX + 12,
      y: e.clientY + 12,
    }));
  };

  return (
    <div
      className="chest-gui"
      ref={containerRef}
      style={{ "--scale": scale }} // Expose scale as a CSS variable for all child elements
    >
      {/* Chest title bar — supports Minecraft colour codes via ColorParse */}
      <div className="chest-title-overlay">
        {config.title ? ColorParse(config.title) : "Chest"}
      </div>

      <div className="slots-container">
        <div
          className="slots-grid"
          style={{
            gridTemplateRows: `repeat(${rowCount}, 1fr)`,
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
          }}
        >
          {slotItems.map((slot, i) => {
            const item = slot?.item ?? null;
            const key = slot?.key ?? null;
            const texture = getItemTexture(item?.material);
            const isFillSlot = fillIndices.has(i);
            const showTooltip = shouldShowTooltip(item, isFillSlot);
            return (
              <div
                key={i}
                className="slot"
                // Clicking a non-fill slot selects that item for editing in the sidebar
                onClick={() => {
                  if (item && !isFillSlot && key) {
                    setSelectedItem(key);
                  }
                }}
                onMouseEnter={(e) => {
                  setHoveredSlot(i); // Reveals slot controls (number, add/remove buttons)
                  if (showTooltip) {
                    setTooltip({
                      visible: true,
                      x: e.clientX + 12,
                      y: e.clientY + 12,
                      content: getTooltipContent(item),
                    });
                  }
                }}
                onMouseLeave={() => {
                  setHoveredSlot(null);
                  setTooltip({ visible: false, x: 0, y: 0, content: null });
                }}
                onMouseMove={handleMouseMove}
                style={{ position: "relative" }}
              >
                {/* Slot controls — only visible while this slot is hovered */}
                {hoveredSlot === i && (
                  <>
                    {/* Slot index label (top-left corner) */}
                    <div className="slot-number">{i}</div>

                    {/* Remove button — only shown for explicitly placed (non-fill) items */}
                    {item && !isFillSlot && (
                      <button
                        className="slot-remove"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the slot's onClick (item select)
                          const newLayout = { ...config.layout };
                          delete newLayout[i];
                          setConfig({ ...config, layout: newLayout });
                        }}
                        title="Remove item"
                      >
                        x
                      </button>
                    )}

                    {/* Add button — shown on empty or fill slots when an item is selected in the sidebar */}
                    {( !item || isFillSlot ) && selectedItem && (
                      <button
                        className="slot-add"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newLayout = {
                            ...config.layout,
                            [i]: [selectedItem], // Assign the currently selected item key to this slot
                          };
                          setConfig({ ...config, layout: newLayout });
                        }}
                        title={`Add ${selectedItem}`}
                      >
                        +
                      </button>
                    )}
                  </>
                )}

                {/* Item texture image — pixelated rendering preserves the Minecraft pixel art look */}
                {texture && (
                  <img
                    src={texture.url}
                    alt={item?.name || "item"}
                    className="slot-item"
                    // Head textures are scaled down slightly so the face fits neatly inside the slot
                    style={texture.isHead ? { transform: "scale(0.8)", transformOrigin: "center" } : {}}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Minecraft-style tooltip, rendered in a fixed position so it floats above all UI.
          Starts hidden (visibility: hidden) while useLayoutEffect measures its width and
          decides whether to flip it left of the cursor to avoid viewport overflow. */}
      {tooltip.visible && (
        <div
          ref={tooltipRef}
          className="minecraft-tooltip"
          style={{
            top: tooltip.y,
            left: tooltipLeft ?? tooltip.x, // Falls back to raw cursor x until measurement is done
            position: "fixed",
            pointerEvents: "none",
            whiteSpace: "pre-line",
            zIndex: 1000,
            visibility: tooltipLeft !== null ? "visible" : "hidden",
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
}
